import React, { useState } from 'react';
import { X, Archive, RotateCcw, AlertTriangle } from 'lucide-react';
import Button from '../UI/Button';
import ActionConfirmationModal from '../UI/ActionConfirmationModal';
import { useAppSelector } from '../../store/hooks';

interface EventDestructiveActionsProps {
  eventStatus: string;
  eventTitle: string;
  onCancelEvent?: (reason: string) => void;
  onArchiveEvent?: () => void;
  onUnarchiveEvent?: () => void;
  disabled?: boolean;
}

export const EventDestructiveActions: React.FC<EventDestructiveActionsProps> = ({
  eventStatus,
  eventTitle,
  onCancelEvent,
  onArchiveEvent,
  onUnarchiveEvent,
  disabled = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [showModal, setShowModal] = useState<'cancel' | 'archive' | 'restore' | null>(null);

  // Modal handlers
  const handleConfirmAction = (reason?: string) => {
    if (!showModal) return;
    
    switch (showModal) {
      case 'cancel':
        if (reason) {
          onCancelEvent?.(reason);
        }
        break;
      case 'archive':
        onArchiveEvent?.();
        break;
      case 'restore':
        onUnarchiveEvent?.();
        break;
    }
    setShowModal(null);
  };

  const handleCloseModal = () => {
    setShowModal(null);
  };

  const handleShowModal = (action: 'cancel' | 'archive' | 'restore') => {
    setShowModal(action);
  };

  // Don't render if no destructive actions are available
  const hasActions = 
    (onCancelEvent && ['active', 'rescheduled'].includes(eventStatus)) ||
    (onArchiveEvent && ['active', 'rescheduled'].includes(eventStatus)) ||
    (onUnarchiveEvent && eventStatus === 'archived');

  if (!hasActions) {
    return null;
  }

  return (
    <>
    <div className={`mt-8 p-6 border-2 border-dashed rounded-xl ${
      theme === 'dark' 
        ? 'border-red-800/50 bg-red-900/10' 
        : 'border-red-200 bg-red-50'
    }`}>
      <div className="flex items-start">
        <AlertTriangle className={`h-6 w-6 mr-3 mt-0.5 ${
          theme === 'dark' ? 'text-red-400' : 'text-red-600'
        }`} />
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-2 ${
            theme === 'dark' ? 'text-red-300' : 'text-red-800'
          }`}>
            Destructive Actions
          </h3>
          <p className={`text-sm mb-4 ${
            theme === 'dark' ? 'text-red-400' : 'text-red-600'
          }`}>
            These actions will permanently change the event status and cannot be easily undone.
          </p>
          
          <div className="flex flex-wrap gap-3">
            {/* Cancel Event */}
            {onCancelEvent && ['active', 'rescheduled'].includes(eventStatus) && (
              <Button
                variant="danger"
                onClick={() => handleShowModal('cancel')}
                disabled={disabled}
                leftIcon={<X className="h-4 w-4" />}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
                title="Cancel this event permanently"
              >
                Cancel Event
              </Button>
            )}

            {/* Archive Event */}
            {onArchiveEvent && ['active', 'rescheduled'].includes(eventStatus) && (
              <Button
                variant="outline"
                onClick={() => handleShowModal('archive')}
                disabled={disabled}
                leftIcon={<Archive className="h-4 w-4" />}
                className={`${
                  theme === 'dark'
                    ? 'text-gray-300 border-gray-600 hover:bg-gray-800 hover:text-white'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title="Archive this event"
              >
                Archive Event
              </Button>
            )}

            {/* Unarchive Event */}
            {onUnarchiveEvent && eventStatus === 'archived' && (
              <Button
                variant="outline"
                onClick={() => handleShowModal('restore')}
                disabled={disabled}
                leftIcon={<RotateCcw className="h-4 w-4" />}
                className={`${
                  theme === 'dark'
                    ? 'text-gray-300 border-gray-600 hover:bg-gray-800 hover:text-white'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title="Restore this event from archive"
              >
                Restore Event
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Confirmation Modal */}
    <ActionConfirmationModal
      isOpen={showModal !== null}
      onClose={handleCloseModal}
      onConfirm={handleConfirmAction}
      action={showModal || 'cancel'}
      eventTitle={eventTitle}
    />
    
  </>
  );
};

export default EventDestructiveActions;
